import cn from 'classnames';

import ImageLazyLoader from 'components/common/ImageLazyLoader';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import { makeHashFromString } from 'helpers/index';

import css from 'styles/components/landing/landingPageImage.scss';

// static media queries are ok, they align with what is returned in the data.
// this is somewhat temporary, as we redesign image components to match Figma, we'd prefer static aspect ratios rather than being so dynamic.
export const makeAspectRatioStyleInnerHtml = ({ id, mobileX, mobileY, tabletX, tabletY, srcX, srcY }) => {
  const style = `
      ${
        mobileX && mobileY
          ? `@media (max-width: 650px) {
            #${id} {
              aspect-ratio: ${mobileX}/${mobileY};
              object-fit: cover;
            }
      }`
          : ''
      }

      ${
        tabletX && tabletY
          ? `@media (min-width: 651px) and (max-width: 1024px) {
            #${id} {
              aspect-ratio: ${tabletX}/${tabletY};
              object-fit: cover;
            }
      }`
          : ''
      }

      ${
        srcX && srcY
          ? `@media (min-width: 1025px) {
            #${id} {
              aspect-ratio: ${srcX}/${srcY};
              object-fit: cover;
            }
      }`
          : ''
      }
 `;

  return style;
};

const SrcSet = ({ media, srcset, bp, reg, ret }) => {
  const mediaAttribute = media ? media : `(max-width: ${bp})`;
  if (!reg && !srcset) {
    return null;
  }
  if (!srcset) {
    srcset = reg;
    if (ret) {
      srcset += `, ${ret} 2x`;
    }
  }
  return <source key={bp + reg} media={mediaAttribute} srcSet={srcset} />;
};

/**
 * Render images with different src files depending on what SiteMerch populates in Symphony
 */
export const LandingPageImage = ({
  src = null,
  srcset = null,
  mobilesrc = null,
  tabletsrc = null,
  retina = null,
  mobileretina = null,
  tabletretina = null,
  mobileSrcSetBreakpoint = '650px',
  tabletSrcSetBreakpoint = '1024px',
  sources = null,
  alt = null,
  title = null,
  width = null,
  height = null,
  role = null,
  itemProp = null,
  shouldLazyLoad = false,
  className = null,
  imgTestId = null,
  isFullWidth = false,
  mobilesrc_x: mobileX,
  mobilesrc_y: mobileY,
  tabletsrc_x: tabletX,
  tabletsrc_y: tabletY,
  src_x: srcX,
  src_y: srcY,
  fetchPriority,
  shouldStyleAspectRatio
}) => {
  let pictureProps;
  if (sources?.length) {
    pictureProps = sources.map(({ media, srcset }) => <SrcSet key={srcset + media} media={media} srcset={srcset} />);
  } else {
    pictureProps = [
      <SrcSet bp={mobileSrcSetBreakpoint} key={mobileSrcSetBreakpoint} reg={mobilesrc} ret={mobileretina} />,
      <SrcSet bp={tabletSrcSetBreakpoint} key={tabletSrcSetBreakpoint} reg={tabletsrc} ret={tabletretina} />
    ];
  }

  const altString = !alt || alt === '""' ? '' : alt;
  const srcSet = srcset || (retina ? `${retina} 2x, ${src} 1x` : null);
  const imgProps = {
    src,
    title,
    width,
    height,
    srcSet,
    role,
    itemProp,
    'alt': altString,
    'data-test-id': imgTestId
  };

  if (src) {
    let imgId;
    let aspectRatioStyle;
    if (srcX) {
      imgId = makeHashFromString(src);
      aspectRatioStyle = makeAspectRatioStyleInnerHtml({ id: imgId, mobileX, mobileY, tabletX, tabletY, srcX, srcY });
    }

    if (shouldLazyLoad) {
      return (
        <>
          {shouldStyleAspectRatio ? <style dangerouslySetInnerHTML={{ __html: aspectRatioStyle }} /> : undefined}
          <ImageLazyLoader
            id={imgId}
            className={cn(className, { [css.fullWidth]: isFullWidth, [css.hasStyledAspectRatio]: shouldStyleAspectRatio })}
            imgProps={{ ...imgProps, id: imgId }}
            pictureProps={pictureProps}
          />
        </>
      );
    } else {
      return (
        <>
          {shouldStyleAspectRatio ? <style dangerouslySetInnerHTML={{ __html: aspectRatioStyle }} /> : undefined}
          <picture className={isFullWidth ? css.fullWidth : null}>
            {pictureProps}
            <img
              id={imgId}
              className={cn({ [css.fullWidth]: isFullWidth, [css.hasStyledAspectRatio]: shouldStyleAspectRatio })}
              src={src}
              alt={altString}
              title={title}
              width={width}
              height={height}
              srcSet={srcSet}
              role={role}
              itemProp={itemProp}
              data-test-id={imgTestId}
              fetchpriority={fetchPriority}
            />
          </picture>
        </>
      );
    }
  }
  return null;
};

export default withErrorBoundary('LandingPageImage', LandingPageImage);
